/**
 * Comprehensive SEO Configuration for Mernaki
 * Updated to reflect the full product spectrum: Career Page Builder, Job Portal Generator, Employer Branding Platform
 */

export interface SEOConfig {
  title: string
  description: string
  keywords: string[]
  ogTitle: string
  ogDescription: string
  twitterTitle: string
  twitterDescription: string
  canonical?: string
  structuredData?: object
}

// Base SEO Configuration
export const baseSEO = {
  siteName: 'Mernaki',
  siteUrl: 'https://mernaki.de',
  defaultImage: 'https://mernaki.de/android-chrome-512x512.png',
  twitterHandle: '@mernaki',
  locale: 'en_US',
  shortTagline: 'Mernaki — Modern Career Pages, Hiring Sites & Employer Branding Tools',
  primaryDescription: 'Mernaki helps companies create premium career pages, branded job sites and modern hiring experiences in minutes. No developers needed. Generate beautiful job portals, upload applications, customize themes, connect forms, and manage candidates — all in one Apple-grade interface.',
  extendedDescription: 'Mernaki is a next-generation recruitment platform combining design, hiring automation, job page templates, employer branding, and custom integrations. Build high-converting career pages, streamline applications, manage jobs, and deliver a modern candidate experience that feels as premium as your brand. Ideal for small teams, fast-growing companies, agencies, and global enterprises.',
}

// Primary Keywords - Core product features
export const primaryKeywords = [
  'career page builder',
  'job page generator',
  'recruitment website creator',
  'employer branding platform',
  'hiring page templates',
  'custom job site builder',
  'company job portal generator',
  'recruitment page builder',
  'job portal generator',
  'branding UX hiring platform',
]

// Secondary Keywords - Supporting features
export const secondaryKeywords = [
  'modern recruitment templates',
  'applicant submission page',
  'branded hiring experience',
  'job website template',
  'HR landing page builder',
  'recruitment SaaS platform',
  'no-code career page builder',
  'mini ATS for small companies',
  'applicant tracking system',
  'creative theme customization platform',
  'Apple-style SaaS website toolkit',
  'candidate management system',
  'job application forms',
  'recruitment automation platform',
]

// Long-tail Keywords - User intent (super powerful for SEO)
export const longTailKeywords = [
  'how to create a careers page for my company',
  'best job page builder for small companies',
  'branded job site templates for HR teams',
  'no-code career page builder',
  'company hiring website templates',
  'modern career page examples',
  'recruitment website builder for startups',
  'custom job portal without developers',
  'employer branding tools for small teams',
  'applicant tracking for growing companies',
]

// Page-specific SEO Configurations
export const pageSEO: Record<string, SEOConfig> = {
  // Home Page
  home: {
    title: 'Mernaki — Modern Career Page Builder & Branded Hiring Sites',
    description: 'Mernaki helps companies create premium career pages, branded job sites and modern hiring experiences in minutes. No developers needed. Generate beautiful job portals, upload applications, customize themes, connect forms, and manage candidates — all in one Apple-grade interface.',
    keywords: [...primaryKeywords, ...secondaryKeywords.slice(0, 8)],
    ogTitle: 'Mernaki — Modern Career Page Builder & Branded Hiring Sites',
    ogDescription: 'Build premium career pages, branded job sites and modern hiring experiences in minutes. No developers needed. Generate beautiful job portals, customize themes, and manage candidates.',
    twitterTitle: 'Mernaki — Modern Career Page Builder',
    twitterDescription: 'Create beautiful career pages and job portals without code. Modern design, custom themes, streamlined applications, and candidate management.',
    canonical: 'https://mernaki.de',
  },

  // Products Page
  products: {
    title: 'Mernaki Templates & Custom Career Pages — Company Job Portal Builder',
    description: 'Create beautiful, high-converting career pages and job portals without code. Choose a template, customize branding, integrate forms, manage applications, and publish instantly. Complete recruitment platform with ATS features, custom themes, and integrations.',
    keywords: ['career page templates', 'job portal builder', 'recruitment templates', 'hiring page design', 'custom job site', 'employer branding templates', 'applicant tracking', 'recruitment automation', 'job application management', 'ATS for small companies'],
    ogTitle: 'Mernaki Templates & Custom Career Pages — Job Portal Builder',
    ogDescription: 'Create beautiful, high-converting career pages and job portals without code. Templates, branding, forms, applicant management, and ATS features.',
    twitterTitle: 'Mernaki Templates & Career Pages',
    twitterDescription: 'Choose a template, customize branding, integrate forms, manage applications, and publish instantly. Complete recruitment platform.',
    canonical: 'https://mernaki.de/products',
  },

  // About Page
  about: {
    title: 'About Mernaki — Redefining Employer Branding & Recruitment Design',
    description: 'Mernaki is a next-generation recruitment platform that helps companies create premium career pages, branded hiring experiences, and modern job portals. Learn about our mission to transform how companies attract talent.',
    keywords: ['about mernaki', 'employer branding platform', 'recruitment design', 'career page platform', 'hiring technology'],
    ogTitle: 'About Mernaki — Modern Recruitment Platform',
    ogDescription: 'Learn how Mernaki is transforming employer branding and recruitment with premium career pages and modern hiring experiences.',
    twitterTitle: 'About Mernaki',
    twitterDescription: 'Transforming employer branding with premium career pages and modern hiring experiences.',
    canonical: 'https://mernaki.de/about',
  },

  // Careers Page
  careers: {
    title: 'Careers at Mernaki — Join the Future of Hiring & Employer Branding',
    description: 'Join Mernaki and help shape the future of recruitment technology. We\'re building the next generation of career page builders and employer branding tools.',
    keywords: ['mernaki careers', 'recruitment tech jobs', 'employer branding jobs', 'saas careers', 'tech startup jobs'],
    ogTitle: 'Careers at Mernaki — Join Our Team',
    ogDescription: 'Help us build the future of recruitment technology. Join our team creating premium career pages and employer branding tools.',
    twitterTitle: 'Careers at Mernaki',
    twitterDescription: 'Join our team building the future of recruitment technology.',
    canonical: 'https://mernaki.de/careers',
  },

  // Team Page
  team: {
    title: 'Meet the Mernaki Team — Designers, Engineers & Recruitment Experts',
    description: 'Meet the team behind Mernaki. We\'re designers, engineers, and recruitment experts passionate about creating beautiful hiring experiences.',
    keywords: ['mernaki team', 'recruitment platform team', 'employer branding experts', 'career page designers'],
    ogTitle: 'Meet the Mernaki Team',
    ogDescription: 'Designers, engineers, and recruitment experts building the future of employer branding.',
    twitterTitle: 'Meet the Mernaki Team',
    twitterDescription: 'The team building the future of employer branding and recruitment design.',
    canonical: 'https://mernaki.de/team',
  },

  // Partners Page
  partners: {
    title: 'Mernaki Partners — Integrations, Agencies & Recruitment Tools',
    description: 'Explore Mernaki partner integrations, agency partnerships, and recruitment tool connections. Build a complete hiring ecosystem.',
    keywords: ['mernaki partners', 'recruitment integrations', 'hiring tool integrations', 'ats integrations', 'hr software partners'],
    ogTitle: 'Mernaki Partners & Integrations',
    ogDescription: 'Explore partner integrations and build a complete hiring ecosystem with Mernaki.',
    twitterTitle: 'Mernaki Partners',
    twitterDescription: 'Partner integrations for a complete hiring ecosystem.',
    canonical: 'https://mernaki.de/partners',
  },

  // Partnerships Page
  partnerships: {
    title: 'Partner with Mernaki — Agency & Integration Partnership Program',
    description: 'Join the Mernaki partnership program. Become an agency partner, integration partner, or reseller. Grow your business with our career page platform.',
    keywords: ['mernaki partnership', 'agency partnership', 'recruitment partner program', 'saas partnership', 'integration partner'],
    ogTitle: 'Partner with Mernaki',
    ogDescription: 'Join our partnership program. Become an agency partner, integration partner, or reseller.',
    twitterTitle: 'Partner with Mernaki',
    twitterDescription: 'Join our partnership program and grow your business.',
    canonical: 'https://mernaki.de/partnerships',
  },

  // Booking Page
  booking: {
    title: 'Book a Demo — See Mernaki Career Page Builder in Action',
    description: 'Schedule a personalized demo of Mernaki. See how easy it is to create premium career pages, customize themes, and launch your branded hiring site.',
    keywords: ['mernaki demo', 'career page builder demo', 'recruitment platform demo', 'book consultation', 'hiring site demo'],
    ogTitle: 'Book a Mernaki Demo',
    ogDescription: 'Schedule a personalized demo and see how easy it is to create premium career pages.',
    twitterTitle: 'Book a Mernaki Demo',
    twitterDescription: 'See Mernaki in action. Schedule your personalized demo today.',
    canonical: 'https://mernaki.de/booking',
  },

  // Contact Page
  contact: {
    title: 'Contact Mernaki — Get Help with Your Career Page & Hiring Site',
    description: 'Contact Mernaki support for help with career pages, job portals, templates, and employer branding. We\'re here to help you create amazing hiring experiences.',
    keywords: ['contact mernaki', 'career page support', 'recruitment platform help', 'hiring site support', 'employer branding help'],
    ogTitle: 'Contact Mernaki Support',
    ogDescription: 'Get help with career pages, job portals, and employer branding. Our team is here to help.',
    twitterTitle: 'Contact Mernaki',
    twitterDescription: 'Get help with your career pages and hiring sites.',
    canonical: 'https://mernaki.de/contact',
  },

  // Help Center
  help: {
    title: 'Mernaki Help Center — Career Page Builder Guides & Support',
    description: 'Find answers about Mernaki career page builder, job portal templates, customization options, and employer branding features. Comprehensive guides and tutorials.',
    keywords: ['mernaki help', 'career page builder guide', 'job portal tutorial', 'recruitment platform support', 'hiring site help'],
    ogTitle: 'Mernaki Help Center',
    ogDescription: 'Comprehensive guides and tutorials for building premium career pages and job portals.',
    twitterTitle: 'Mernaki Help Center',
    twitterDescription: 'Guides and tutorials for building premium career pages.',
    canonical: 'https://mernaki.de/help',
  },

  // FAQ Page
  faq: {
    title: 'FAQ — Frequently Asked Questions About Mernaki Career Page Builder',
    description: 'Find quick answers about Mernaki career page builder, pricing, templates, customization, integrations, and employer branding features.',
    keywords: ['mernaki faq', 'career page builder questions', 'job portal faq', 'recruitment platform questions', 'hiring site faq'],
    ogTitle: 'Mernaki FAQ — Your Questions Answered',
    ogDescription: 'Quick answers about career page builder, templates, pricing, and employer branding features.',
    twitterTitle: 'Mernaki FAQ',
    twitterDescription: 'Quick answers about our career page builder and features.',
    canonical: 'https://mernaki.de/faq',
  },

  // Documentation
  docs: {
    title: 'Mernaki Documentation — Career Page Builder API & Developer Guides',
    description: 'Complete Mernaki documentation for developers. API reference, integration guides, custom themes, webhooks, and advanced career page customization.',
    keywords: ['mernaki documentation', 'career page api', 'recruitment platform api', 'job portal api', 'developer docs', 'integration guide'],
    ogTitle: 'Mernaki Documentation',
    ogDescription: 'Complete developer documentation, API reference, and integration guides.',
    twitterTitle: 'Mernaki Documentation',
    twitterDescription: 'Developer docs, API reference, and integration guides.',
    canonical: 'https://mernaki.de/docs',
  },

  // API Reference
  apiReference: {
    title: 'Mernaki API Reference — Career Page Builder REST API Documentation',
    description: 'Complete REST API documentation for Mernaki career page builder. Create job postings, manage applications, customize themes, and integrate with your systems.',
    keywords: ['mernaki api', 'career page api', 'recruitment api', 'job posting api', 'applicant tracking api', 'rest api documentation'],
    ogTitle: 'Mernaki API Reference',
    ogDescription: 'Complete REST API documentation for career pages, job postings, and application management.',
    twitterTitle: 'Mernaki API Reference',
    twitterDescription: 'REST API documentation for career pages and job management.',
    canonical: 'https://mernaki.de/developer/api',
  },

  // Status Page
  status: {
    title: 'Mernaki System Status — Career Page Builder Uptime & Performance',
    description: 'Real-time status of Mernaki career page builder, API, and services. Check uptime, performance metrics, and incident history.',
    keywords: ['mernaki status', 'career page builder uptime', 'system status', 'api status', 'service health'],
    ogTitle: 'Mernaki System Status',
    ogDescription: 'Real-time status, uptime, and performance metrics for Mernaki services.',
    twitterTitle: 'Mernaki Status',
    twitterDescription: 'Real-time system status and uptime monitoring.',
    canonical: 'https://mernaki.de/status',
  },

  // Report Issue
  reportIssue: {
    title: 'Report an Issue — Mernaki Career Page Builder Support',
    description: 'Report a bug, issue, or problem with Mernaki career page builder. Our team will investigate and resolve your issue quickly.',
    keywords: ['report issue', 'mernaki bug', 'career page problem', 'technical support', 'bug report'],
    ogTitle: 'Report an Issue — Mernaki Support',
    ogDescription: 'Report bugs or issues with Mernaki. Our team will help resolve them quickly.',
    twitterTitle: 'Report an Issue',
    twitterDescription: 'Report bugs or issues with Mernaki.',
    canonical: 'https://mernaki.de/report-issue',
  },

  // Privacy Policy
  privacyPolicy: {
    title: 'Privacy Policy — Mernaki Career Page Builder Data Protection',
    description: 'Mernaki privacy policy. Learn how we protect your data, handle applicant information, and ensure GDPR compliance for career pages and job portals.',
    keywords: ['mernaki privacy', 'data protection', 'gdpr compliance', 'privacy policy', 'applicant data privacy'],
    ogTitle: 'Mernaki Privacy Policy',
    ogDescription: 'Learn how we protect your data and ensure GDPR compliance.',
    twitterTitle: 'Mernaki Privacy Policy',
    twitterDescription: 'Data protection and GDPR compliance information.',
    canonical: 'https://mernaki.de/privacy-policy',
  },

  // Terms of Service
  termsOfService: {
    title: 'Terms of Service — Mernaki Career Page Builder Legal Terms',
    description: 'Mernaki terms of service. Legal terms, conditions, and agreements for using our career page builder and recruitment platform.',
    keywords: ['mernaki terms', 'terms of service', 'legal terms', 'service agreement', 'platform terms'],
    ogTitle: 'Mernaki Terms of Service',
    ogDescription: 'Legal terms and conditions for using Mernaki career page builder.',
    twitterTitle: 'Mernaki Terms of Service',
    twitterDescription: 'Legal terms and service agreements.',
    canonical: 'https://mernaki.de/terms-of-service',
  },

  // Cookie Policy
  cookiePolicy: {
    title: 'Cookie Policy — Mernaki Career Page Builder Cookie Usage',
    description: 'Mernaki cookie policy. Learn how we use cookies to improve your experience with our career page builder and recruitment platform.',
    keywords: ['mernaki cookies', 'cookie policy', 'tracking cookies', 'website cookies', 'privacy cookies'],
    ogTitle: 'Mernaki Cookie Policy',
    ogDescription: 'Learn how we use cookies to improve your experience.',
    twitterTitle: 'Mernaki Cookie Policy',
    twitterDescription: 'Cookie usage and privacy information.',
    canonical: 'https://mernaki.de/cookies',
  },

  // Impressum
  impressum: {
    title: 'Impressum — Mernaki Legal Information & Company Details',
    description: 'Mernaki impressum. Legal information, company details, and contact information as required by German law.',
    keywords: ['mernaki impressum', 'legal information', 'company details', 'german legal', 'imprint'],
    ogTitle: 'Mernaki Impressum',
    ogDescription: 'Legal information and company details.',
    twitterTitle: 'Mernaki Impressum',
    twitterDescription: 'Legal information and company details.',
    canonical: 'https://mernaki.de/impressum',
  },
}

// Helper function to get SEO config for a page
export const getSEOConfig = (page: string): SEOConfig => {
  return pageSEO[page] || pageSEO.home
}

// Helper function to generate full meta tags
export const generateMetaTags = (config: SEOConfig) => {
  return {
    title: config.title,
    description: config.description,
    keywords: config.keywords.join(', '),
    canonical: config.canonical,
    openGraph: {
      title: config.ogTitle,
      description: config.ogDescription,
      url: config.canonical,
      siteName: baseSEO.siteName,
      images: [
        {
          url: baseSEO.defaultImage,
          width: 512,
          height: 512,
          alt: 'Mernaki Logo',
        },
      ],
      locale: baseSEO.locale,
      type: 'website',
    },
    twitter: {
      card: 'summary_large_image',
      title: config.twitterTitle,
      description: config.twitterDescription,
      site: baseSEO.twitterHandle,
      images: [baseSEO.defaultImage],
    },
  }
}
